// PRODUCTION — admin flow editor single step row inside a step group card
import React, { Fragment } from 'react'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import type { OnboardingTemplateStep } from 'src/interfaces/onboardingTemplate'
import {
  FLOW_EDITOR_FEATURE_FLAGS,
  isFlowEditorStepDeletable,
} from 'src/constants/flowEditorFeatureFlags'
import { stepLabel } from 'src/constants/collect-data/stepTypeLabels'
import {
  isDropTargetInDraggedStepGroup,
  isFlowEditorStepMovable,
  type FlowEditorStepDropTarget,
} from 'src/utils/collectDataTemplateEditor/stepReorder'

export interface FlowEditorStepGroupStepRowProps {
  draggedStepConfigId: string | null
  groupConfigId: string
  draggedStepId: string | null
  handleDragEnd: () => void
  handleDragOver: (event: React.DragEvent, dropTarget: FlowEditorStepDropTarget) => void
  handleDragStart: (stepId: string) => void
  handleDrop: (dropTarget: FlowEditorStepDropTarget) => void
  handleRemoveStep: (stepId: string) => void
  pendingDeleteId: string | null
  selectedStepId: string | null
  setPendingDeleteId: (id: string | null) => void
  setSelectedGroupConfigId: (id: string | null) => void
  setSelectedStepId: (id: string | null) => void
  step: OnboardingTemplateStep
  stepIndexInGroup: number
  unsavedStepIds: Set<string>
}

export function FlowEditorStepGroupStepRow({
  draggedStepConfigId,
  groupConfigId,
  draggedStepId,
  handleDragEnd,
  handleDragOver,
  handleDragStart,
  handleDrop,
  handleRemoveStep,
  pendingDeleteId,
  selectedStepId,
  setPendingDeleteId,
  setSelectedGroupConfigId,
  setSelectedStepId,
  step,
  stepIndexInGroup,
  unsavedStepIds,
}: FlowEditorStepGroupStepRowProps) {
  const allowReorder = isFlowEditorStepMovable(step)
  const allowDelete = FLOW_EDITOR_FEATURE_FLAGS.allowDeleteSteps && isFlowEditorStepDeletable(step)
  const isSelected = selectedStepId === step.id
  const dropTarget: FlowEditorStepDropTarget = {
    configId: groupConfigId,
    indexInGroup: stepIndexInGroup,
  }
  const isDragging = allowReorder && draggedStepId === step.id
  const isDropZoneActive =
    draggedStepId !== null &&
    draggedStepId !== step.id &&
    isDropTargetInDraggedStepGroup(draggedStepConfigId, dropTarget)
  const isPendingDelete = allowDelete && pendingDeleteId === step.id

  let stepRowCss = tw`hover:bg-shades-50`

  if (isSelected) {
    stepRowCss = tw`bg-primary-100`
  } else if (isDragging) {
    stepRowCss = tw`bg-shades-50 opacity-40`
  }

  const stepIndex = stepIndexInGroup

  return (
    <div
      draggable={allowReorder}
      onDragStart={
        allowReorder
          ? () => {
              handleDragStart(step.id)
            }
          : undefined
      }
      onDragOver={
        isDropZoneActive
          ? (event) => {
              handleDragOver(event, dropTarget)
            }
          : undefined
      }
      onDrop={
        isDropZoneActive
          ? () => {
              handleDrop(dropTarget)
            }
          : undefined
      }
      onDragEnd={allowReorder ? handleDragEnd : undefined}
      onClick={() => {
        if (!isPendingDelete) {
          setSelectedGroupConfigId(null)
          setSelectedStepId(isSelected ? null : step.id)
          setPendingDeleteId(null)
        }
      }}
      css={[tw`flex cursor-pointer items-center gap-2 px-3 py-2.5 transition-colors`, stepRowCss]}
    >
      {isPendingDelete ? (
        <Fragment>
          <span
            css={tw`min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[13px] font-medium text-shades-700`}
          >
            Remove?
          </span>
          <div css={tw`flex flex-shrink-0 gap-1`} onClick={(event) => event.stopPropagation()}>
            <Button
              variant="outlined"
              size="tiny"
              color="white"
              onClick={() => setPendingDeleteId(null)}
              className={tw`!border-shades-200 !text-shades-700 hover:!bg-shades-100`}
            >
              Cancel
            </Button>
            <Button
              size="tiny"
              color="primary"
              onClick={() => handleRemoveStep(step.id)}
              className={tw`!bg-critical-500 hover:!bg-critical-600 active:!bg-critical-600`}
            >
              Delete
            </Button>
          </div>
        </Fragment>
      ) : (
        <Fragment>
          {allowReorder && (
            <div
              css={tw`flex flex-shrink-0 cursor-grab items-center px-0.5`}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <svg width="12" height="16" viewBox="0 0 12 16" css={tw`fill-shades-300`}>
                <circle cx="3" cy="3" r="1.5" />
                <circle cx="9" cy="3" r="1.5" />
                <circle cx="3" cy="8" r="1.5" />
                <circle cx="9" cy="8" r="1.5" />
                <circle cx="3" cy="13" r="1.5" />
                <circle cx="9" cy="13" r="1.5" />
              </svg>
            </div>
          )}

          <span
            css={[
              tw`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold`,
              isSelected ? tw`bg-primary-200 text-primary-700` : tw`bg-shades-100 text-shades-500`,
            ]}
          >
            {stepIndex + 1}
          </span>

          <div css={tw`flex min-w-0 flex-1 flex-col gap-0.5 overflow-hidden`}>
            <span
              css={[
                tw`overflow-hidden text-ellipsis whitespace-nowrap text-[13px]`,
                isSelected ? tw`font-semibold text-primary-700` : tw`font-medium text-shades-800`,
              ]}
            >
              {stepLabel(step.type)}
            </span>
            {step.collectDataTemplateStepLabel !== step.type && (
              <span
                css={tw`overflow-hidden text-ellipsis whitespace-nowrap text-[11px] font-medium text-shades-500`}
                title={step.collectDataTemplateStepLabel}
              >
                {step.collectDataTemplateStepLabel}
              </span>
            )}
          </div>

          {allowDelete && (
            <Button
              size="tiny"
              color="grey"
              onClick={(event) => {
                event.stopPropagation()
                setPendingDeleteId(step.id)
              }}
              leftIcon={
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="red"
                  strokeWidth={2.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6M14 11v6" />
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
              }
            />
          )}
          {unsavedStepIds.has(step.id) && (
            <span
              aria-label="Unsaved changes on this step"
              title="Unsaved changes on this step"
              css={tw`h-1.5 w-1.5 flex-shrink-0 self-center rounded-full bg-primary-500`}
            />
          )}
        </Fragment>
      )}
    </div>
  )
}
